import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fileUrl } from "@/lib/files";
import { fmtDate } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { DocumentDialog } from "@/components/employes/DocumentDialog";
import { useEmployes, EtatBadge } from "@/components/employes/shared";
import { FileText, Eye, Plus, Trash2, Loader2 } from "lucide-react";

const LABELS = { contrat: "Contrat de travail", convention_sante: "Convention santé", analyse: "Analyse médicale", fiche_paie: "Fiche de paie" };

const Row = ({ d, paie, editable, onDelete }) => (
  <tr data-testid={`doc-row-${d.id}`}>
    <td className="py-2 pr-2 font-medium">{d.employe_nom}</td>
    <td className="py-2 pr-2">{LABELS[d.type]}</td>
    <td className="py-2 pr-2">{d.titre}</td>
    <td className="py-2 pr-2">{fmtDate(d.date_document)}</td>
    <td className="py-2 pr-2">{paie ? (d.periode || "—") : d.date_expiration ? fmtDate(d.date_expiration) : "—"}</td>
    {!paie && <td className="py-2 pr-2"><EtatBadge statut={d.statut} jours={d.jours_restants} testid={`doc-statut-${d.id}`} /></td>}
    <td className="py-2 text-right whitespace-nowrap">
      {d.file_id && <Button size="sm" variant="outline" className="gap-1" onClick={() => window.open(fileUrl(d.file_id), "_blank")} data-testid={`doc-view-${d.id}`}><Eye className="h-4 w-4" /> Voir</Button>}
      {editable && <Button size="sm" variant="ghost" onClick={() => onDelete(d)} data-testid={`doc-delete-${d.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
    </td>
  </tr>
);

export default function EmployeDocuments({ types, title, subtitle }) {
  const { items: employes, editable, q } = useEmployes();
  const [docs, setDocs] = useState(null);
  const [add, setAdd] = useState(false);
  const paie = types.includes("fiche_paie");
  const load = () => api.get(`/employes/documents/list?types=${types.join(",")}&${q}`).then((r) => setDocs(r.data)).catch(() => setDocs([]));
  useEffect(() => { setDocs(null); load(); }, [q, types.join()]); // eslint-disable-line
  const head = ["Employé", "Type", "Intitulé", "Date", paie ? "Mois de paie" : "Expiration", ...(paie ? [] : ["Statut"])];

  const onDelete = async (d) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    await api.delete(`/employes/documents/${d.id}`);
    toast.success("Document supprimé");
    load();
  };

  return (
    <div data-testid={`docs-page-${types[0]}`}>
      <PageHeader title={title} subtitle={subtitle}
        actions={editable && <Button className="gap-1" disabled={!employes?.length} onClick={() => setAdd(true)} data-testid="doc-add-btn"><Plus className="h-4 w-4" /> Ajouter un document</Button>} />
      <div className="rounded-xl border bg-card p-5">
        {docs === null ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          : !docs.length ? <EmptyState icon={FileText} title="Aucun document" hint={employes?.length ? "Ajoutez un document PDF ou une photo." : "Créez d'abord vos employés dans « Dates d'embauche »."} />
            : <div className="overflow-x-auto"><table className="w-full text-sm min-w-[800px]">
              <thead><tr className="text-left text-muted-foreground border-b">{head.map((h) => <th key={h} className="py-2 pr-2 font-medium">{h}</th>)}<th /></tr></thead>
              <tbody className="divide-y">{docs.map((d) => <Row key={d.id} d={d} paie={paie} editable={editable} onDelete={onDelete} />)}</tbody>
            </table></div>}
      </div>
      {add && <DocumentDialog types={types} labels={LABELS} employes={employes} onClose={() => setAdd(false)} onSaved={() => { setAdd(false); load(); }} />}
    </div>
  );
}
